import React from 'react';
import {tokens} from '../../theme/tokens';
import {getReadableText} from '../../utils/ColorHelper';

export type ScoreMeterSize = 'sm' | 'md' | 'lg';

interface ScoreMeterProps {
  value: number;
  max: number;
  color?: string;
  label?: string;
  count?: boolean;
  size?: ScoreMeterSize;
}

// Per-size dimensions lifted from the design's ImpactIndicator meter style.
const DIMS: Record<ScoreMeterSize, {fs: number; gap: number; meterW: number; meterH: number}> = {
  sm: {fs: 12, gap: 6, meterW: 7, meterH: 11},
  md: {fs: 13, gap: 7, meterW: 8, meterH: 14},
  lg: {fs: 15, gap: 9, meterW: 10, meterH: 18},
};

// Backend colors may arrive without a leading '#'.
const normalizeHex = (color: string | undefined, fallback: string): string =>
  !color ? fallback : color.startsWith('#') ? color : `#${color}`;

const ScoreMeter: React.FC<ScoreMeterProps> = ({value, max, color, label, count = false, size = 'md'}) => {
  if (!max) return null;

  const dims = DIMS[size];
  const track = tokens.colors.borderStrong;
  const accent = normalizeHex(color, track);
  const filled = Math.round(value);
  // Label color echoes the accent as a darkened, readable hue (the design's per-level "text" tone).
  const textColor = color ? getReadableText(accent) : tokens.colors.textSecondary;
  const heavy = size === 'lg';

  const wordCount = label ? (count ? `${label} · ${value}/${max}` : label) : null;

  return (
    <span
      className="gt-score-meter"
      role="meter"
      aria-label={label}
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      style={{gap: dims.gap}}
    >
      <span className="gt-score-meter-track" style={{gap: 2.5}}>
        {Array.from({length: max}, (_, i) => {
          const on = i < filled;
          return (
            <span
              key={i}
              className="gt-score-meter-segment"
              style={{
                width: dims.meterW,
                height: on ? dims.meterH : dims.meterH * 0.55,
                background: on ? accent : track,
              }}
            />
          );
        })}
      </span>
      {wordCount && (
        <span
          className="gt-score-meter-label"
          style={{fontSize: dims.fs, color: textColor, fontWeight: heavy ? 700 : 400}}
        >
          {wordCount}
        </span>
      )}
    </span>
  );
};

export default ScoreMeter;
